import styles from './Pagination.module.scss';

/**
 * Pagination 컴포넌트
 * - 페이지네이션 공용 컴포넌트
 * - 현재 페이지, 전체 페이지 수, 페이지 변경 핸들러를 props로 받음
 * - totalItems와 pageSize를 받으면 자동으로 표시 여부 결정 (16개 이상일 때만 표시)
 *
 * @example
 * // 방법 1: totalItems와 pageSize 사용 (권장) - 16개 이상일 때만 자동 표시
 * <Pagination
 *   currentPage={currentPage}
 *   totalItems={vacationList.length}
 *   pageSize={15}
 *   onPageChange={handlePageChange}
 * />
 *
 * @example
 * // 방법 2: totalPages 직접 전달 (기존 방식) - totalPages > 1일 때 표시
 * <Pagination
 *   currentPage={currentPage}
 *   totalPages={5}
 *   onPageChange={handlePageChange}
 * />
 *
 * @example
 * // 방법 3: 커스텀 핸들러 사용
 * <Pagination
 *   currentPage={currentPage}
 *   totalItems={data.length}
 *   pageSize={10}
 *   onPageChange={handlePageChange}
 *   onFirstPage={() => setCurrentPage(1)}
 *   onPrevPage={() => setCurrentPage(currentPage - 1)}
 *   onNextPage={() => setCurrentPage(currentPage + 1)}
 *   onLastPage={() => setCurrentPage(totalPages)}
 * />
 */
export default function Pagination({
  currentPage = 1,
  totalPages = 1,
  totalItems,
  pageSize,
  onPageChange,
  onFirstPage,
  onPrevPage,
  onNextPage,
  onLastPage,
}) {
  // totalItems와 pageSize가 제공되면 자동으로 계산
  const calculatedTotalPages =
    totalItems && pageSize ? Math.ceil(totalItems / pageSize) : totalPages;

  // totalItems와 pageSize가 제공되면 16개 이상일 때만 표시, 아니면 totalPages > 1일 때 표시
  const shouldShow = totalItems && pageSize ? totalItems > pageSize : calculatedTotalPages > 1;

  // 표시하지 않으면 null 반환
  if (!shouldShow) {
    return null;
  }

  // 페이지 변경 시 스크롤을 상단으로 이동
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleFirstPage() {
    if (onFirstPage) {
      onFirstPage();
    } else if (onPageChange) {
      onPageChange(1);
    }
    scrollToTop();
  }

  function handlePrevPage() {
    if (onPrevPage) {
      onPrevPage();
    } else if (onPageChange && currentPage > 1) {
      onPageChange(currentPage - 1);
    }
    scrollToTop();
  }

  function handleNextPage() {
    if (onNextPage) {
      onNextPage();
    } else if (onPageChange && currentPage < calculatedTotalPages) {
      onPageChange(currentPage + 1);
    }
    scrollToTop();
  }

  function handleLastPage() {
    if (onLastPage) {
      onLastPage();
    } else if (onPageChange) {
      onPageChange(calculatedTotalPages);
    }
    scrollToTop();
  }

  function handlePageClick(page) {
    if (onPageChange) {
      onPageChange(page);
    }
    scrollToTop();
  }

  // 표시할 페이지 번호들 생성 (최대 5개)
  function getPageNumbers() {
    const pages = [];

    const maxPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
    const endPage = Math.min(calculatedTotalPages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(i);
    }

    return pages;
  }

  const pageNumbers = getPageNumbers();

  return (
    <div className={styles.pagination}>
      <div className={styles.pageNavGroup}>
        <button
          type="button"
          className={styles.pageNavButton}
          onClick={handleFirstPage}
          disabled={currentPage === 1}
          aria-label="처음"
        >
          <span className={`${styles.pageNavIcon} ${styles.pageNavIconFirst}`} />
        </button>
        <button
          type="button"
          className={styles.pageNavButton}
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          aria-label="이전"
        >
          <span className={`${styles.pageNavIcon} ${styles.pageNavIconPrev}`} />
        </button>
      </div>
      <div className={styles.pageNumbers}>
        {pageNumbers.map(function (page) {
          return (
            <button
              key={page}
              type="button"
              className={`${styles.pageNumber} ${currentPage === page ? styles.pageNumberActive : ''}`}
              onClick={function () {
                handlePageClick(page);
              }}
            >
              {page}
            </button>
          );
        })}
      </div>
      <div className={styles.pageNavGroup}>
        <button
          type="button"
          className={styles.pageNavButton}
          onClick={handleNextPage}
          disabled={currentPage === calculatedTotalPages}
          aria-label="다음"
        >
          <span className={`${styles.pageNavIcon} ${styles.pageNavIconNext}`} />
        </button>
        <button
          type="button"
          className={styles.pageNavButton}
          onClick={handleLastPage}
          disabled={currentPage === calculatedTotalPages}
          aria-label="마지막"
        >
          <span className={`${styles.pageNavIcon} ${styles.pageNavIconLast}`} />
        </button>
      </div>
    </div>
  );
}
